import { MainContext } from "components/utils/Contexts";
import React, { useContext, useRef, useState } from "react";
import {
  MdArrowForward,
  MdBolt,
  MdMoreHoriz,
  MdOutlineCollectionsBookmark,
} from "react-icons/md";
import { GoAlertFill } from "react-icons/go";
import { Course, Program } from "utils/interfaces";
import useMouseClickOutside from "hooks/MouseClickOutside";
import EditDeleteMenu from "../components/EditDeleteMenu";
import ImageSkeleton from "utils/skeletons/ImageSkeleton";

export interface ProgramCardProps {
  program: Program;
  handleExpand: () => void;
  handleEdit: (event: React.MouseEvent) => void;
  handleDelete: (event: React.MouseEvent) => void;
}

const ProgramCard: React.FC<ProgramCardProps> = ({
  program,
  handleExpand,
  handleEdit,
  handleDelete,
}) => {
  // States definition
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  // Create a menu ref
  const menuRef: React.RefObject<HTMLDivElement> = useRef(null);

  // Definition of the maximum number of courses to show on the card
  const maxCoursesToShow = 3;

  // Get the context to prevent caching
  const { userRole } = useContext(MainContext);

  /**
   * Hook used when clicking outside the dropdown menu
   */
  useMouseClickOutside(
    menuRef,
    () => setShowOptionsMenu(false),
    showOptionsMenu,
  );

  // Define the activateEditButton value
  const activateEditButton = program.canEdit || false;

  /**
   * Define a deactivated function in order to prevent to execute the
   * function that underlies a button
   * @param event
   */
  const deactivatedFunction = (event: React.MouseEvent) => {
    // Prevent clicking the card
    event.stopPropagation();
  };

  /**
   * Function used to open the pop up menu with the edit and
   * delete option
   * @param event
   */
  const handleClickOptions = (event: React.MouseEvent) => {
    // First of all, prevent clicking the card
    event.stopPropagation();

    // Toggle the state
    setShowOptionsMenu(!showOptionsMenu);
  };

  /**
   * Function used to wrap the edit or delete functions
   * in order to add custom functionalities per card
   */
  const handleClickOptionWrapper = (
    func: (event: React.MouseEvent) => void,
  ) => {
    return (event: React.MouseEvent) => {
      // Close the menu when clicking on one of the options
      setShowOptionsMenu(false);

      // And execute the function
      func(event);
    };
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`self-stretch p-6 ${
        program.profanityFlag ? "border-2 border-warning-500" : ""
      } bg-neutral-25 rounded-xl justify-between items-center inline-flex gap-6 cursor-pointer hover:shadow-card ease-in-out duration-150 relative`}
      onClick={handleExpand}
    >
      <div className="w-[292px] h-[122px] justify-start items-center gap-3 inline-flex">
        {program.profanityFlag && (
          <div className="absolute left-0 top-0 size-8 z-20">
            <div
              className="relative w-full h-full p-1 rounded-tl-lg rounded-br-xl bg-warning-500 inline-flex items-center justify-center"
              title="This program has content flagged as profane"
            >
              <GoAlertFill className="size-5 text-white" />
            </div>
          </div>
        )}
        <div className="w-[122px] h-[122px] justify-center items-center flex">
          <ImageSkeleton
            src={program.headerImage}
            alt={program.title}
            className="w-[122px] h-[122px] relative"
          />
        </div>
        <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
          <div className="max-h-10 w-full text-neutral-700 text-base font-semibold font-sans leading-tight line-clamp-2">
            {program.title || "-"}
          </div>
          <div className="w-full text-neutral-600 text-xs font-normal font-sans leading-[18px] line-clamp-4">
            {program.description || "-"}
          </div>
        </div>
      </div>
      <div className="p-1 rounded flex-col justify-start items-start gap-3 inline-flex">
        <div className="self-stretch h-[99px] flex-col justify-center items-center gap-1 flex">
          <MdBolt className="w-8 h-8 fill-neutral-700" />
          <div className="flex-col justify-center items-center gap-1 flex">
            <div className="w-[41px] text-center text-neutral-700 text-lg font-semibold leading-snug">
              {program.experiencePoints || 0}
            </div>
            <div className="pb-[3px] flex-col justify-center items-end gap-2 flex">
              <div className="justify-center items-end gap-2 inline-flex">
                <div className="text-center text-neutral-500 text-[10px] font-normal uppercase leading-3 tracking-wide">
                  Knowledge points
                </div>
              </div>
            </div>
          </div>
          {/* <div className="self-stretch h-0.5 bg-neutral-200 flex-col justify-start items-start gap-2 flex">
            <div
              className={`w-[79px] h-0.5 ${getProgressBarColor(expPoints)}`}
              style={{ width: `${expPoints / 10}%` }}
            />
          </div> */}
        </div>
      </div>
      <div className="w-[165px] p-1 rounded flex-col justify-start items-start gap-1 inline-flex">
        <div className="self-stretch h-[66px] flex-col justify-center items-center gap-1 flex">
          <div className="p-2 rounded-full justify-start items-end gap-2 inline-flex">
            <MdOutlineCollectionsBookmark className="w-8 h-8 fill-neutral-700" />
          </div>
          <div className="self-stretch justify-start items-center gap-2 inline-flex">
            <div className="grow shrink basis-0 text-center text-neutral-500 text-xs font-bold uppercase leading-[14px] tracking-wide">
              Courses
            </div>
          </div>
          <div className="self-stretch h-[30px] flex-col justify-start items-start gap-0.5 flex">
            <div className="self-stretch justify-center items-center flex-col inline-flex">
              {program.courses && program.courses.length > 0 ? (
                program.courses.map((course: Course, index: number) => {
                  if (index <= maxCoursesToShow) {
                    return (
                      <div
                        key={index}
                        className="grow shrink basis-0 text-center text-neutral-500 text-xs font-normal font-sans leading-[14px] whitespace-nowrap"
                      >
                        {index === maxCoursesToShow ? "..." : course.title}
                      </div>
                    );
                  }
                })
              ) : (
                <div className="grow shrink basis-0 text-center text-neutral-500 text-xs font-normal font-sans leading-[14px]">
                  No courses defined yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="w-fit self-stretch justify-center items-center gap-3 flex">
        {["master admin", "customer admin", "class admin"].includes(
          userRole,
        ) && (
          <div
            className="w-12 self-stretch justify-center items-center gap-3 inline-flex relative"
            ref={menuRef}
          >
            <button
              className={`p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex z-20 ${
                activateEditButton
                  ? "hover:bg-neutral-200 ease-in-out duration-300 cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={
                activateEditButton ? handleClickOptions : deactivatedFunction
              }
              title={
                activateEditButton
                  ? "More options"
                  : "This user is not allowed to edit this program"
              }
            >
              <MdMoreHoriz
                className={`w-6 h-6 ${
                  activateEditButton ? "fill-neutral-700" : "fill-neutral-200"
                }`}
              />
            </button>
            <EditDeleteMenu
              show={showOptionsMenu}
              handleEdit={handleClickOptionWrapper(handleEdit)}
              handleDelete={handleClickOptionWrapper(handleDelete)}
              containerStyle="absolute w-fit top-[112px] right-0 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
            />
          </div>
        )}
        {["class admin", "parent", "student"].includes(userRole) && (
          <button
            className="p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex cursor-pointer z-20 hover:bg-neutral-200 ease-in-out duration-300"
            onClick={() => {}}
            title="Continue"
          >
            <MdArrowForward className="w-6 h-6 fill-neutral-700" />
          </button>
        )}
        {/* {["customer admin", "class admin"].includes(userRole) && (
        <div
            className="p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex cursor-pointer"
            onClick={handleAddToClasses}
          >
            <div className="w-6 h-6">
              <MdOutlineAddToPhotos className="w-full h-full fill-neutral-700" />
            </div>
          </div>
      )} */}
      </div>
    </div>
  );
};

export default ProgramCard;
